<template>

  <MainSiteLayout class="ProductGuidePage" v-bind="{ content }">

    <BasicHero :content="{ title: content.owningGuide?.title, breadcrumbs }" />

    <ProductGuideLayout>

      <div class="body">
        <h2 class="heading">{{ content.title }}</h2>
        <BodyContent v-if="content.body" :blocks="content.body" class="bodyContent" />
      </div>

      <NextPrevPageLinks v-if="nextPrevPageLinks" v-bind="nextPrevPageLinks" class="nextPrevPageLinks" />

      <template v-slot:sidebar>
        <ProductGuideSidebar v-bind="{ resourceGroupMenu }" />
      </template>

      <template v-slot:bottom>
        <ContactPane v-if="content.contactPane" :content="content.contactPane" class="contactPane" />
      </template>

    </ProductGuideLayout>

  </MainSiteLayout>

</template>

<script>
export const contentQuery = `
  *[_id == $pageId][0]{
    _id,
    locale,
    title,
    body,
    'contactPane': *[_type == 'supportContactPane' && locale == ^.locale][0],
    'owningGuide': *[_id == $guideId][0]{
      _id,
      title,
      sections,
      owningGroup->{
        title,
        resources[] {
          _type == 'download' => {
            _key,
            _type,
            title,
            url,
          },
          _type == 'guide' => {
            _key,
            _type,
            _ref,
            'title': @->title,
            'landingSectionId': @->landingSection._ref,
          },
        },
      },
    },
    'sectionTitles': *[_type == 'supportSection' && locale == ^.locale] { _id, title },
  }
`
</script>

<script setup>
import { provide } from 'vue'

import MainSiteLayout from '../../layouts/MainSiteLayout.vue'

import BodyContent from '../body-content/BodyContent.vue'

import BasicHero from '../shared/BasicHero.vue'
import ContactPane from '../shared/ContactPane.vue'

import ProductGuideLayout from './ProductGuideLayout.vue'
import ProductGuideSidebar from './ProductGuideSidebar.vue'
import NextPrevPageLinks from './NextPrevPageLinks.vue'

function getOwningGuideContents({ _id, owningGuide, sectionTitles }, { sectionPathsById }) {
  function replaceSectionRefs(sectionOrSubsections) {
    switch (sectionOrSubsections._type) {
    case 'subsections':
      const sections = sectionOrSubsections.sections.map(replaceSectionRefs)
      return {
        _key: sectionOrSubsections._key,
        _type: sectionOrSubsections._type,
        title: sectionOrSubsections.title,
        sections,
        containsCurrentSection:
          sections.some(
            (entry) => entry.isCurrentSection || entry.containsCurrentSection
          ),
      }
    case 'section':
      const sectionId = sectionOrSubsections._ref
      const { title } = sectionTitles.find((data) => data._id == sectionId)
      return {
        _key: sectionOrSubsections._key,
        _type: sectionOrSubsections._type,
        _id: sectionId,
        title,
        path: sectionPathsById[sectionId],
        isCurrentSection: sectionId === _id,
      }
    }
  }
  return (owningGuide?.sections || []).map(replaceSectionRefs)
}

function getBreadcrumbs(ancestorBreadcrumbs, context) {
  const indexPageBreadcrumb = ancestorBreadcrumbs[ancestorBreadcrumbs.length - 1]
  const productBreadcrumb = {
    title: context.product.title,
    path: `${indexPageBreadcrumb.path}?product=${context.product.name}`,
  }
  const groupBreadcrumb = {
    title: context.group.title,
  }
  const guideBreadcrumb = {
    title: context.guide.title,
  }
  return (
    context.group.title
      ? [indexPageBreadcrumb, productBreadcrumb, groupBreadcrumb, guideBreadcrumb]
      : [indexPageBreadcrumb, productBreadcrumb, guideBreadcrumb]
  )
}

function getResourceGroupMenu(content, context) {
  return (content.owningGuide?.owningGroup?.resources || []).map(
    (resource) => {
      switch (resource._type) {
      case 'download':
        return resource
      case 'guide':
        const guideResource = {
          '_key': resource._key,
          '_type': resource._type,
          'title': resource.title,
        }
        if (resource._ref === content.owningGuide._id) {
          guideResource.contents = getOwningGuideContents(content, context)
        }
        else {
          guideResource.path = context.landingSectionPathsById[resource.landingSectionId]
        }
        return guideResource
      }
    }
  )
}

function getNextPrevPageLinks(content, context) {
  function listPages(sectionOrSubsections) {
    switch (sectionOrSubsections._type) {
    case 'section':
      return sectionOrSubsections
    case 'subsections':
      return sectionOrSubsections.sections.flatMap(listPages)
    }
  }
  const allPagesInGuide = getOwningGuideContents(content, context).flatMap(listPages)
  const currentPageIndex = allPagesInGuide.findIndex((entry) => entry._id === content._id)
  if (currentPageIndex !== -1) {
    return {
      next:
        currentPageIndex < allPagesInGuide.length - 1
          ? allPagesInGuide[currentPageIndex + 1]
          : null,
      prev:
        currentPageIndex > 0
          ? allPagesInGuide[currentPageIndex - 1]
          : null,
    }
  }
}

const { ancestorBreadcrumbs, content, context } = defineProps({
  ancestorBreadcrumbs: {
    type: Array,
    default: Array,
  },
  content: {
    type: Object,
    default: Object,
  },
  context: {
    type: Object,
    default: Object,
  },
})

const breadcrumbs = getBreadcrumbs(ancestorBreadcrumbs, context)
const resourceGroupMenu = getResourceGroupMenu(content, context)
const nextPrevPageLinks = getNextPrevPageLinks(content, context)

provide('locale', content.locale)
provide('context', context)
</script>

<style scoped>

.heading {
  font-weight: 600;
  font-size: var(--font-size-7);
  line-height: 1.2;
  color: var(--text-color-black);
}

.bodyContent {
  margin-top: var(--spacing-7);
}

.nextPrevPageLinks {
  padding-top: var(--spacing-9);
  border-top: thin solid var(--hairline-color);
}

.contactPane {
  margin-top: var(--spacing-17);
}
</style>
